import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Package } from 'lucide-react';
import { useGenerationStore } from '@/stores/generation';
import { BriefsListView } from '@/pages/BriefsPage';
import { BriefWorkspace } from './workspace/BriefWorkspace';
import { useCreativeWorkbench } from '../CreativeWorkbenchContext';
import { BriefSearchBar, type ExtendedFilters } from './BriefSearchBar';
import { BriefCreatorOverlay } from '@/components/brief/BriefCreatorOverlay';

const DEFAULT_EXTENDED: ExtendedFilters = {
  channel: [],
  source_type: [],
  performance_bucket: null,
  strategic_playbook_id: [],
  group_by: 'flat',
};

export function BriefsView() {
  const { briefId } = useParams<{ briefId: string }>();
  const navigate = useNavigate();
  const { clientUid, personaId } = useCreativeWorkbench();
  const { briefFilters, setBriefFilters, fetchBriefs, fetchPlaybooks } = useGenerationStore();

  const [extended, setExtended] = useState<ExtendedFilters>(DEFAULT_EXTENDED);
  const [showCreateModal, setShowCreateModal] = useState(false);

  const nextClient = clientUid ?? '';
  const nextPersona = personaId ?? '';

  // Sync workbench client/persona → brief store, then trigger a fetch.
  useEffect(() => {
    if (briefId) return;
    const driftedClient = briefFilters.client_uid !== nextClient;
    const driftedPersona = briefFilters.persona_id !== nextPersona;
    if (driftedClient || driftedPersona) {
      setBriefFilters({ client_uid: nextClient, persona_id: nextPersona });
    } else {
      fetchBriefs();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [briefId, nextClient, nextPersona]);

  // Fetch playbooks whenever the playbook tab is activated.
  useEffect(() => {
    if (briefId) return;
    if (extended.group_by === 'playbook') {
      fetchPlaybooks();
    }
  }, [briefId, extended.group_by, fetchPlaybooks]);

  // Focus Cmd+F on the search input.
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if ((e.metaKey || e.ctrlKey) && e.key === 'f') {
        const input = document.querySelector<HTMLInputElement>(
          'input[placeholder^="Search"]',
        );
        if (input) {
          e.preventDefault();
          input.focus();
          input.select();
        }
      }
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  const handleOpenBrief = (id: string) => navigate(`/creative/briefs/${id}`);
  const handleBack = () => navigate('/creative/briefs');

  if (briefId) {
    return <BriefWorkspace briefId={briefId} onBack={handleBack} />;
  }

  return (
    <div className="space-y-5">
      {/* ── Page Header ── */}
      <div className="flex items-center gap-2">
        <Package size={18} className="text-teal-500" />
        <h1 className="text-xl font-bold text-navy-900">Creative Briefs</h1>
      </div>

      {/* ── Search Bar + New Brief ── */}
      <BriefSearchBar
        query={briefFilters.search}
        onQueryChange={(q) => setBriefFilters({ search: q })}
        status={briefFilters.status}
        onStatusChange={(s) => setBriefFilters({ status: s })}
        extended={extended}
        onExtendedChange={setExtended}
        onNewBrief={() => setShowCreateModal(true)}
      />

      {/* ── List: forwards groupBy + search down to BriefsListView ── */}
      <BriefsListView
        onOpenBrief={handleOpenBrief}
        embedded
        groupBy={extended.group_by}
        searchQuery={briefFilters.search}
      />

      {/* ── New Brief Modal ── */}
      <BriefCreatorOverlay
        open={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        onBriefCreated={(briefId) => {
          setShowCreateModal(false);
          navigate(`/creative/briefs/${briefId}`);
        }}
        defaultClientUid={clientUid || undefined}
        defaultPersonaId={personaId || undefined}
      />
    </div>
  );
}
